<script setup lang="ts">
import { useForm } from 'vee-validate'
import { z } from 'zod'

import { BtnOrange } from '~/components/ui/Buttons'
import VCheckbox from '~/components/ui/Inputs/VCheckbox.vue'
import VTextInput from '~/components/ui/Inputs/VText.vue'

import { useSegmentIdentify } from '~/composables/Segment/useSegmentIdentify'
import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useCustomerIO } from '~/composables/useCustomerIO'
import {
  defaultFieldValidationSettings,
  useFieldValidation,
} from '~/composables/useFieldValidation'
import { useProvideAuthFetch } from '~/composables/useProvideAuthFetch'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useUtmTracking } from '~/composables/useUtmTracking'

import { useLoginStore } from '~/stores/login'
import { useUserStore } from '~/stores/user'

import { setGrowthBookAttributes } from '~/helpers/GrowthBookHelpers'

import { provideAuthApiLogin } from '~/api-auth/Login'
import { provideAuthApiLogout } from '~/api-auth/Logout'
import { provideAuthApiRegister, registerCallFailed } from '~/api-auth/Register'
import {
  provideLoginSession,
  provideLogoutSession,
} from '~/api-core/Login/Session'

import type { UserState } from '~/stores/user'
import type { Locale } from '~/types/locale'

export type ExpandedUser = Pick<
  UserState,
  'email' | 'first_name' | 'last_name' | 'lang' | 'id' | 'is_active'
> & { password: string }

type Props = {
  modelValue: null | UserState
}
type Emits = {
  'update:model-value': [newUser: ExpandedUser | null]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { $growthBook } = useNuxtApp()
const { locale } = useI18n()
const route = useRoute()

const { coreFetch } = useProvideCoreFetch()
const { authFetch } = useProvideAuthFetch()
const { utmUserTracking } = useUtmTracking()
const { validate: validateForm } = useForm()
const segmentIdentify = useSegmentIdentify()
const segmentTrack = useSegmentTrack()
const { customerIOIdentify } = useCustomerIO()

const {
  value: firstNameFieldValue,
  errorMessage: firstNameFieldErrorMessage,
  handleBlur: firstNameFieldValidateOnBlur,
} = useFieldValidation('userFirstName', z.string().min(1), {
  ...defaultFieldValidationSettings(props.modelValue?.first_name || ''),
  syncVModel: false,
})
const {
  value: lastNameFieldValue,
  errorMessage: lastNameFieldErrorMessage,
  handleBlur: lastNameFieldValidateOnBlur,
} = useFieldValidation('userLastName', z.string().min(1), {
  ...defaultFieldValidationSettings(props.modelValue?.last_name || ''),
  syncVModel: false,
})
const {
  value: emailFieldValue,
  errorMessage: emailFieldErrorMessage,
  handleBlur: emailFieldValidateOnBlur,
} = useFieldValidation('userEmail', z.string().email(), {
  ...defaultFieldValidationSettings(props.modelValue?.email || ''),
  syncVModel: false,
})
const {
  value: passwordFieldValue,
  errorMessage: passwordFieldErrorMessage,
  handleBlur: passwordFieldValidateOnBlur,
} = useFieldValidation('userPassword', z.string().min(8), {
  ...defaultFieldValidationSettings(''),
  syncVModel: false,
})
// CGS === Terms of services in french
const {
  value: cgsFieldValue,
  errorMessage: cgsFieldErrorMessage,
  handleBlur: cgsFieldValidateOnBlur,
} = useFieldValidation(
  'userCgs',
  z.boolean().refine((value) => !!value),
  { ...defaultFieldValidationSettings(false), syncVModel: false },
)

const id = ref(0)
const displayPswd = ref(false)
const loading = ref(false)

const passwordInputType = computed<'text' | 'password'>(() =>
  displayPswd.value ? 'text' : 'password',
)

onMounted(updateParent)

const { SET_DISPLAY: LOGIN_SET_DISPLAY } = useLoginStore()
const { SET: SET_USER, RESET: RESET_USER } = useUserStore()

const endUserObject = computed(() => ({
  password: passwordFieldValue.value,
  email: emailFieldValue.value,
  first_name: firstNameFieldValue.value,
  last_name: lastNameFieldValue.value,
  lang: locale.value as Locale,
  id: id.value,
  is_active: true,
}))

function updateParent(): void {
  if (id.value) emit('update:model-value', endUserObject.value)
  else emit('update:model-value', null)
}

function requestLogin(): void {
  LOGIN_SET_DISPLAY(true)
}

function trackSignupWithSegment(userId: number): void {
  const signupData = {
    has_come_from_referral: route.path.includes('referral'),
    has_come_from_widget: route.query.utm_source === 'widget',
    sign_up_method: 'email' as const,
  }

  segmentIdentify({
    ...signupData,
    user_id: userId,
    date_sign_up: new Date(),
  })

  segmentTrack('Sign Up - Sign Up Finalized', {
    ...signupData,
    has_come_from_curator_dash: false, // shouldn't have this cause it's a curator signup
    widget_curator_name: undefined, // shouldn't have this cause it's a curator signup
    referrer_agency_id: undefined, // same as above
    landing_page_url: route.path,
    landing_page_accessed_from: window.localStorage.getItem(
      'comesFromCustomSignup',
    )
      ? 'CDBaby'
      : 'Groover',
  })
}

async function v2CreateUser() {
  try {
    const logoutFromCore = provideLogoutSession(coreFetch)
    const logoutFromAuth = provideAuthApiLogout(authFetch)
    const registerUserOnAuth = provideAuthApiRegister(authFetch)
    const logUserToCore = provideLoginSession(coreFetch)
    const loginToAuth = provideAuthApiLogin(authFetch)

    const email = emailFieldValue.value.toLowerCase().trim()

    const registerResponse = await registerUserOnAuth({
      accepted_tos: true,
      first_name: firstNameFieldValue.value,
      lang: locale.value as Locale,
      email,
      password1: passwordFieldValue.value,
      password2: passwordFieldValue.value,
      profile_type: 'curator',
    })

    await Promise.allSettled([logoutFromCore(), logoutFromAuth()])

    const [user] = await Promise.all([
      logUserToCore(registerResponse.access),
      loginToAuth({
        email,
        password: passwordFieldValue.value,
      }),
    ])
    RESET_USER()
    return user
  } catch (error) {
    if (!authFetch.$isFetchError(error)) throw new Error('Failed register')

    const registerFailedData = error.data
    if (registerCallFailed(registerFailedData)) {
      emailFieldErrorMessage.value = registerFailedData.email?.[0] || undefined
      passwordFieldErrorMessage.value =
        registerFailedData.password1?.[0] ||
        registerFailedData.password2?.[0] ||
        undefined
      firstNameFieldErrorMessage.value =
        registerFailedData.first_name?.[0] || undefined
      lastNameFieldErrorMessage.value =
        registerFailedData.last_name?.[0] || undefined
    }

    throw new Error('Failed register')
  }
}

async function createUser() {
  if (loading.value) return

  const { valid } = await validateForm()
  if (!valid) return

  loading.value = true

  try {
    const user = await v2CreateUser()

    if (!user) return

    SET_USER(user)
    id.value = user.id

    utmUserTracking().catch()
    setGrowthBookAttributes({ userId: user.id }, $growthBook)
    customerIOIdentify(user.id, {
      email: user.email,
      created_at: Date.now(),
    })
    trackSignupWithSegment(user.id)

    await nextTick()
    updateParent()
    loading.value = false
  } catch (_) {
    loading.value = false
  }
}
</script>

<template>
  <div class="tw-grid tw-grid-cols-1 tw-gap-6">
    <h2 class="tw-text-h5 tw-text-center">
      {{ $t('auth.createUser00') }}
    </h2>
    <VTextInput
      v-model="firstNameFieldValue"
      data-test-id="curatorSignupFirstNameInput"
      :p-validity="!firstNameFieldErrorMessage"
      :placeholder="$t('auth.createUser01')"
      @blur="firstNameFieldValidateOnBlur($event, true)"
      @enter="createUser"
      ><template #err-message>{{
        firstNameFieldErrorMessage
      }}</template></VTextInput
    >
    <VTextInput
      v-model="lastNameFieldValue"
      data-test-id="curatorSignupLastNameInput"
      :placeholder="$t('auth.createUser02')"
      :p-validity="!lastNameFieldErrorMessage"
      @blur="lastNameFieldValidateOnBlur($event, true)"
      @enter="createUser"
      ><template #err-message>{{
        lastNameFieldErrorMessage
      }}</template></VTextInput
    >
    <VTextInput
      v-model="emailFieldValue"
      data-test-id="curatorSignupEmailInput"
      :p-validity="!emailFieldErrorMessage"
      :placeholder="$t('auth.createUser03')"
      left-picto="fas fa-envelope"
      @blur="emailFieldValidateOnBlur($event, true)"
      @enter="createUser"
      ><template #err-message>{{
        emailFieldErrorMessage
      }}</template></VTextInput
    >
    <div class="tw-relative tw-grid tw-gap-1">
      <VTextInput
        v-model="passwordFieldValue"
        data-test-id="curatorSignupPasswordInput"
        :p-validity="!passwordFieldErrorMessage"
        :type="passwordInputType"
        left-picto="fas fa-lock"
        :placeholder="$t('auth.createUser06')"
        @blur="passwordFieldValidateOnBlur($event, true)"
        @enter="createUser"
        ><template #err-message>{{
          passwordFieldErrorMessage
        }}</template></VTextInput
      >
      <div
        class="tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-justify-end tw-text-sm tw-text-orange-500"
        @click="displayPswd = !displayPswd"
      >
        <transition name="fade" mode="out-in">
          <span v-if="displayPswd" key="hide">
            <i class="fa-solid fa-eye-slash"></i>
            <span>{{ $t('auth.createUser04') }}</span>
          </span>
          <span v-else key="display">
            <i class="fa-solid fa-eye"></i>
            <span>{{ $t('auth.createUser05') }}</span>
          </span>
        </transition>
      </div>
    </div>
    <div>
      <VCheckbox
        v-model="cgsFieldValue"
        data-test-id="curatorSignupCguCheckbox"
        :p-validity="!cgsFieldErrorMessage"
        @blur="cgsFieldValidateOnBlur($event, true)"
      >
        <template #label>
          <div>
            <span>{{ $t('auth.createUser07') }}</span>
            <a
              class="tw-text-orange-500 tw-underline"
              :href="`/${locale}/lp/cgs/`"
              target="_blank"
              >{{
                $t('band.signup.init.createUserTemplate.input.cguUnderlined')
              }}</a
            >
          </div>
        </template>
      </VCheckbox>
    </div>
    <BtnOrange
      data-test-id="curatorSignupCreateAccountCTA"
      :loading="loading"
      class="tw-w-full"
      @click="createUser"
    >
      <div class="btnText">
        {{ $t('auth.createUser09') }}
      </div>
    </BtnOrange>
    <div class="tw-w-full tw-text-center">
      <span>{{ $t('auth.createUser10') }}</span>
      <span class="tw-text-orange-500" @click="requestLogin">{{
        $t('auth.createUser11')
      }}</span>
    </div>
  </div>
</template>
